import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * RAD Finance — ThemeProvider
 * --------------------------------------------------------------------------
 * Gerencia o tema (dark/light) do app. Ao montar:
 *   1. Lê preferência persistida em localStorage (`rad-theme`).
 *   2. Sem preferência salva, abre em light (default determinístico do RAD
 *      Finance — não segue o prefers-color-scheme do SO, ao contrário do PBX).
 *
 * A troca é feita escrevendo o atributo `data-theme` no <html>; como todas as
 * cores vêm de CSS variables (tokens.css), a transição é instantânea — sem
 * reload. O index.html aplica o tema persistido antes do React montar para
 * evitar flash de tema errado (FOUC).
 */

export type ThemeMode = 'dark' | 'light';

interface ThemeContextValue {
  theme: ThemeMode;
  toggle: () => void;
  setTheme: (mode: ThemeMode) => void;
}

const STORAGE_KEY = 'rad-theme';
const DEFAULT_THEME: ThemeMode = 'light';

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredTheme(): ThemeMode | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === 'dark' || v === 'light') return v;
  } catch {
    // localStorage pode estar bloqueado (modo privado) — silenciamos.
  }
  return null;
}

function writeStoredTheme(mode: ThemeMode): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // sem persistência: tudo bem, apenas não sobrevive ao reload.
  }
}

function applyDocumentTheme(mode: ThemeMode): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', mode);
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) {
    meta.content = mode === 'dark' ? '#0A1426' : '#F1F5F9';
  }
}

interface ThemeProviderProps {
  children: ReactNode;
  /** Se fornecido, sobrescreve a heurística padrão (útil para testes). */
  defaultTheme?: ThemeMode;
}

export function ThemeProvider({ children, defaultTheme }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    if (defaultTheme) return defaultTheme;
    return readStoredTheme() ?? DEFAULT_THEME;
  });

  useEffect(() => {
    applyDocumentTheme(theme);
  }, [theme]);

  const setTheme = useCallback((mode: ThemeMode): void => {
    setThemeState(mode);
    writeStoredTheme(mode);
  }, []);

  const toggle = useCallback((): void => {
    setThemeState((prev) => {
      const next: ThemeMode = prev === 'dark' ? 'light' : 'dark';
      writeStoredTheme(next);
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, toggle, setTheme }),
    [theme, toggle, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within <ThemeProvider>');
  }
  return ctx;
}
